import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  updateColumnMapping,
  getDataVisualization,
  getReference,
} from "../../network/cipAPI";

import DupontButton from "../../components/DupontButton";
import DupontRadio from "../../components/DupontRadio";
import InputBox from "../../components/InputBox";
import Dropdown from "../../components/Dropdown";
import DataVisualizationTable from "./DataVisualizationTable";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
  WARNING_NOTIFICATION,
} from "../../components/Notifications";
import { OverlayLoader } from "../../components/PageLoading";

import { UpdateSetWithKey } from "../../utils/Utils";
import {
  COLUMN_OPTIONS,
  NORMALIZATION_COLUMN_OPTIONS,
  TRAIN_OPTIONS,
} from "./columns.config";

import { ValueStrings } from "../../utils/constantsString";
import { PASS_STAGE_KEYS } from "../../constants/global.constants";
import { COLUMN_MAPPING_ERROR_KEYS } from "../../constants/global.constants";

import "./index.scss";

const ColumnSelection = ({
  columnsOption,
  setColumnsOptions,
  fileId,
  mappedColumn,
  mappedReference,
  setTempReferenceValues,
  isEdit = false,
  setDatasetDetails,
  plantName,
  water_type,
  selectedRadioColumn,
  setSelectedRadioColumn,
  setMappedReference,
  setIsProceedDisabled,
  setIsNormalizationColumnFilled,
  config,
  selectedTrain,
}) => {
  const { t } = useTranslation();
  const columns = COLUMN_OPTIONS(t);
  const normalizedColumns = NORMALIZATION_COLUMN_OPTIONS(t);

  const { flow, pressure, feed_quality, permeate_quality, temperature } =
    useSelector((state) => state);

  const unitSettings = useMemo(
    () => ({
      flow,
      pressure,
      feed_quality,
      permeate_quality,
      temperature,
    }),
    [flow, pressure, feed_quality, permeate_quality, temperature]
  );

  const [trainscolumns, setTrainscolumns] = useState([]);
  const [showDataVisualizationPopup, setshowDataVisualizationPopup] =
    useState(false);
  const [dataVisualisationRow, setDataVisualisationRow] = useState([]);
  const [dataVisualisationEnabled, setDataVisualisationEnabled] =
    useState(true);
  const [selectedColumns, setselectedColumns] = useState({
    ["sel_train"]: 1,
    ["sel_pass"]: 1,
    ["sel_stage"]: 1,
  });
  const [changedSelectedColumns, setChangedSelectedColumns] = useState({});
  const [changedPassStageFields, setChangedPassStageFields] = useState({});
  // const [selectedRadioColumn, setSelectedRadioColumn] = useState({
  //   ["column5"]: "feed_flowrate",
  //   ["column8"]: "pressure_drop",
  // });
  const [dropdownColumns, setDropdownColumns] = useState(columnsOption);
  const [showOverLayLoader, setShowOverLayLoader] = useState(false);
  const userName = useSelector((state) => state.userName);

  const getOptimizedPayload = (
    mappedColumn,
    changedSelectedColumns,
    changedPassStageFields,
    selectedColumns
  ) => {
    if (mappedColumn) {
      const changedKeys = Object.keys(changedSelectedColumns);
      const passOrStageChanged = PASS_STAGE_KEYS.some((key) =>
        changedKeys.includes(key)
      );
      return {
        ...changedSelectedColumns,
        ...(passOrStageChanged ? changedPassStageFields : {}),
      };
    } else {
      return selectedColumns;
    }
  };

  useEffect(() => {
    setselectedColumns({
      ...mappedColumn,
      ["sel_pressure_vessels"]: "",
      ["sel_element_involved"]: "",
      ["sel_element_type"]: "",
    });
    if (
      mappedColumn &&
      mappedColumn.hasOwnProperty("norm_permeate_flow") &&
      mappedColumn.hasOwnProperty("norm_pressure_drop") &&
      mappedColumn.hasOwnProperty("norm_salt_passage")
    ) {
      setIsNormalizationColumnFilled(true);
    }
    if (!Array.isArray(mappedColumn)) {
      const col5 =
        mappedColumn && mappedColumn.concentrate_flowrate
          ? "concentrate_flowrate"
          : "feed_flowrate";

      const col8 =
        mappedColumn && mappedColumn.pressure_drop
          ? "pressure_drop"
          : "concentrate_pressure";

      setSelectedRadioColumn({
        ...selectedRadioColumn,
        ["column5"]: col5,
        ["column8"]: col8,
      });
    }
    if (isEdit && mappedReference) {
      setDataVisualisationEnabled(false);
    }
  }, [mappedColumn]);

  // useEffect(() => {
  //   setSelectedTrain(getTrainValue(selectedColumns["sel_train"]));
  // }, [selectedColumns]);
  useEffect(() => {
    if (typeof config === "object" && !Array.isArray(config)) {
      const { sel_train, sel_pass, sel_stage } = selectedColumns;
      if (config?.trains.length >= 1) {
        setTrainscolumns(
          TRAIN_OPTIONS(
            t,
            config,
            selectedTrain > 0 ? selectedTrain - 1 : "",
            sel_pass > 0 ? sel_pass - 1 : ""
          )
        );
      }
      if (
        typeof selectedColumns === "object" &&
        !Array.isArray(selectedColumns)
      ) {
        let num_pressure_vessels = "";
        let sel_element_involved = "";
        let ro_nf_element_used = "";

        if (selectedTrain && sel_pass && sel_stage) {
          const train = config.trains.find(
            (op) => op.train_id === parseInt(selectedTrain)
          );
          const pass = train?.passes.find(
            (pass) => pass.pass_id === parseInt(selectedColumns.sel_pass)
          );
          if (sel_stage === "All") {
            let totalPressureVessels = 0;
            let totalNoPressureVessels = 0;
            pass.stages.forEach((element) => {
              totalPressureVessels += +element.num_pressure_vessels;
              const pressureVessels = +element.num_pressure_vessels;
              const numElementsPerPressureVessel =
                +element.num_element_per_pressure_vessel;

              const currentNoPressureVessels =
                pressureVessels * numElementsPerPressureVessel;
              const cumulativeNoPressureVessels =
                currentNoPressureVessels + totalNoPressureVessels;

              totalNoPressureVessels = cumulativeNoPressureVessels;
            });
            num_pressure_vessels = totalPressureVessels;
            sel_element_involved = totalNoPressureVessels;
            ro_nf_element_used = "";
          } else {
            const stage = pass?.stages.find(
              (stage) => stage.stage_id === parseInt(selectedColumns.sel_stage)
            );
            num_pressure_vessels = stage?.num_pressure_vessels || "";
            sel_element_involved =
              stage?.num_pressure_vessels *
                stage?.num_element_per_pressure_vessel || "";
            ro_nf_element_used = stage?.ro_nf_element_used || "";
          }
        } else {
          num_pressure_vessels = "";
          sel_element_involved = "";
          ro_nf_element_used = "";
        }

        setselectedColumns({
          ...selectedColumns,
          ["sel_pressure_vessels"]: num_pressure_vessels,
          ["sel_element_involved"]: sel_element_involved,
          ["sel_element_type"]: ro_nf_element_used,
        });
        // Track auto-calculated field changes
        setChangedPassStageFields({
          ["sel_pressure_vessels"]: num_pressure_vessels,
          ["sel_element_involved"]: sel_element_involved,
          ["sel_element_type"]: ro_nf_element_used,
        });
      }
    }
  }, [
    config,
    t,
    selectedColumns["sel_train"],
    selectedColumns["sel_pass"],
    selectedColumns["sel_stage"],
  ]);

  useEffect(() => {
    if (!fileId) {
      setselectedColumns([]);
      setDataVisualisationEnabled(true);
      setDropdownColumns([]);
      setColumnsOptions([]);
    }
  }, [fileId]);

  useEffect(() => {
    if (columnsOption && Object.keys(columnsOption).length > 0) {
      var filteredOptions = Object.values(columnsOption).filter((obj) => {
        return Object.values(selectedColumns).indexOf(obj) == -1;
      });
      setDropdownColumns(filteredOptions);
    }
  }, [selectedColumns, columnsOption, selectedRadioColumn]);

  const onDropdownValChange = (value, key) => {
    setDataVisualisationEnabled(true);

    setselectedColumns({ ...selectedColumns, [key]: value });
    setChangedSelectedColumns((prevSet) =>
      UpdateSetWithKey(prevSet, key, value)
    );
  };

  const onTrainsValChange = (value, key) => {
    if (key === "sel_train") {
      value = value ? value.toString() : "";

      setselectedColumns({
        ...selectedColumns,
        [key]: value,
        sel_pass: "",
        sel_stage: "",
      });
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, key, value)
      );
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, ["sel_pass"], "")
      );
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, ["sel_stage"], "")
      );
    } else if (key === "sel_pass") {
      setselectedColumns({
        ...selectedColumns,
        [key]: value,
        sel_stage: "",
      });
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, key, value)
      );
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, ["sel_stage"], "")
      );
    } else {
      setselectedColumns({
        ...selectedColumns,
        [key]: value,
      });
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, key, value)
      );
    }
  };

  const getKey = (column) => {
    const key =
      column.options === undefined
        ? column.key
        : selectedRadioColumn[column.key];
    return key;
  };

  const handleOptionChange = (event) => {
    setDataVisualisationEnabled(true);
    const { name, value } = event.target;
    const prevKey = selectedRadioColumn[name];
    delete selectedColumns[prevKey];
    setSelectedRadioColumn({ ...selectedRadioColumn, [name]: value });
  };
  const getchecked = (column, key) => {
    return selectedRadioColumn[column] === key;
  };
  const openDataVisualizationPopup = async () => {
    setShowOverLayLoader(true);
    try {
      const response = await getDataVisualization(fileId, selectedTrain);
      if (response && response.data) {
        if (response.data.detail && response.data.detail.error) {
          setDataVisualisationRow([]);
          createNotification(
            ERROR_NOTIFICATION,
            t("notificationStrings.somethingWrong"),
            ""
          );
        } else {
          setDataVisualisationRow(response.data);
        }
      }
    } catch (err) {
      setDataVisualisationRow([]);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        ""
      );
      console.log("openDataVisualizationPopup", err);
    } finally {
      setShowOverLayLoader(false);
      return setshowDataVisualizationPopup(true);
    }
  };
  const onCloseClicked = async (value) => {
    setshowDataVisualizationPopup(false);
  };
  const getConfirmButtonState = () => {
    const {
      date,
      permeate_flowrate,
      feed_temp,
      permeate_pressure,
      feed_flowrate,
      concentrate_flowrate,
      permeate_quality,
      feed_pressure,
      feed_quality,
      concentrate_pressure,
      pressure_drop,
    } = selectedColumns;

    const hasAllInputs = !!(
      date &&
      permeate_flowrate &&
      feed_temp &&
      permeate_pressure &&
      (feed_flowrate || concentrate_flowrate) &&
      permeate_quality &&
      feed_pressure &&
      feed_quality &&
      (concentrate_pressure || pressure_drop)
    );
    if (
      (!isEdit &&
        hasAllInputs &&
        Object.keys(changedSelectedColumns).length > 0) ||
      (isEdit && hasAllInputs && Object.keys(changedSelectedColumns).length > 0)
    ) {
      return false;
    } else return true;
  };

  const getFormattedDate = (dateString) => {
    const date = moment(dateString).isValid();
    return date ? moment(dateString).format(ValueStrings.dateFormat) : "";
  };

  const onConfirmColumn = async () => {
    setShowOverLayLoader(true);
    try {
      const payload = getOptimizedPayload(
        mappedColumn,
        changedSelectedColumns,
        changedPassStageFields,
        selectedColumns
      );

      const response = await updateColumnMapping(
        fileId,
        payload,
        selectedTrain
      );
      if (response && response.detail === undefined) {
        setDatasetDetails(
          ` ${userName}_ ${plantName}_ ${water_type}
          ${
            getFormattedDate(response.min_date)
              ? `_ ${getFormattedDate(response.min_date)}`
              : ""
          }            
          ${
            getFormattedDate(response.max_date)
              ? `to ${getFormattedDate(response.max_date)}`
              : ""
          }`
        );
        // Check if response.ref is empty, if so call getReference API
        if (!response.ref || response.ref === "") {
          try {
            const referenceResponse = await getReference(selectedTrain);
            if (referenceResponse) {
              setMappedReference(referenceResponse.data);
            } else {
              setMappedReference(response.ref);
            }
          } catch (referenceErr) {
            createNotification(
              ERROR_NOTIFICATION,
              t("notificationStrings.somethingWrong"),
              ""
            );
            setMappedReference(response.ref);
          }
        } else {
          setMappedReference(response.ref);
        }

        setIsProceedDisabled(!response.analysis);
        setDataVisualisationEnabled(true);
        setChangedSelectedColumns({});
        setChangedPassStageFields({});
        createNotification(
          SUCCESS_NOTIFICATION,
          t("analyseData.columnMapped"),
          ""
        );
        setDataVisualisationEnabled(false);
        if (
          payload.hasOwnProperty("norm_permeate_flow") &&
          payload.hasOwnProperty("norm_pressure_drop") &&
          payload.hasOwnProperty("norm_salt_passage")
        ) {
          setIsNormalizationColumnFilled(true);
        }
      } else if (
        response &&
        response.detail &&
        response.detail.key === COLUMN_MAPPING_ERROR_KEYS.NO_VALID_REF_CONDITION
      ) {
        setDataVisualisationEnabled(false);
        createNotification(
          SUCCESS_NOTIFICATION,
          t("analyseData.columnMapped"),
          ""
        );
        createNotification(
          WARNING_NOTIFICATION,
          t("analyseData.noValidReferenceCondition"),
          ""
        );
      } else {
        throw new Error("UNKNOWN_ERROR");
      }
    } catch (err) {
      console.log("Error is " + err);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        ""
      );
      setIsProceedDisabled(true);
    } finally {
      setShowOverLayLoader(false);
    }
  };

  const getTrainValue = (value) => {
    if (value !== undefined && value !== null) {
      if (typeof value === "boolean" || typeof value === "number") {
        return value.toString();
      }
      return value;
    }
  };

  return (
    <div className="lightGrayBorder padding10 position-relative">
      {showOverLayLoader && <OverlayLoader customHeight={"570px"} />}
      <div className="box-title text-align-left">
        {t("analyseData.selectColumn")}
      </div>
      <div className="flex-ai-center container-padding">
        <div className="grid-container-column">
          {Object.keys(columns).map((column, index) => (
            <div className="grid-item-column">
              <div>
                {columns[column].options ? (
                  <div>
                    {Object.values(columns[column].options).map((option) => (
                      <div
                        key={option.key}
                        onChange={handleOptionChange}
                        className="checkbox-container"
                      >
                        <div className="marginBtm5">
                          <DupontRadio
                            text={option.name.replace(
                              "<unit>",
                              `(${unitSettings[option.unit]})`
                            )}
                            fontsize={"font-14"}
                            checked={getchecked(column, option.key)}
                            value={option.key}
                            name={column}
                            isRequired
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <label className="text-left ">
                      {columns[column].name.replace(
                        "<unit>",
                        `(${unitSettings[columns[column].unit]})`
                      )}
                    </label>
                    <span style={{ color: "red" }}>*</span>
                  </>
                )}
              </div>
              <div className="width-dropdown">
                <Dropdown
                  value={selectedColumns[getKey(columns[column])]}
                  options={dropdownColumns}
                  onChange={(value) =>
                    onDropdownValChange(value, getKey(columns[column]))
                  }
                  isRequired
                  showCancelIcon
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="grid-container-column">
          <div className="margin-top-10 ">
            {Object.keys(normalizedColumns).map((normalizedColumn, index) => (
              <div className="grid-item-column marginBtm10">
                <label className="text-left ">
                  {normalizedColumns[normalizedColumn].name.replace(
                    "<unit>",
                    `(${
                      unitSettings[normalizedColumns[normalizedColumn].unit]
                    })`
                  )}
                </label>
                <div className="width-dropdown">
                  <Dropdown
                    value={
                      selectedColumns[
                        getKey(normalizedColumns[normalizedColumn])
                      ]
                    }
                    options={dropdownColumns}
                    onChange={(value) =>
                      onDropdownValChange(
                        value,
                        getKey(normalizedColumns[normalizedColumn])
                      )
                    }
                    showCancelIcon
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="grid-container-trains ">
            {/* <Dropdown
              labelText={t("addNewPlant.sel_train")}
              key={"sel_train"}
              options={createArray(config?.trains?.length, "sel_train", true)}
              value={selectedTrain || 1}
              onChange={(value) => onTrainsValChange(value, "sel_train")}
            /> */}
            {Object.keys(trainscolumns).map((trainscolumn, index) => (
              <div>
                <div>
                  {trainscolumns[trainscolumn].isSelect ? (
                    <Dropdown
                      labelText={trainscolumns[trainscolumn].name}
                      key={trainscolumns[trainscolumn].key}
                      options={trainscolumns[trainscolumn].options}
                      value={getTrainValue(
                        selectedColumns[trainscolumns[trainscolumn].key]
                      )}
                      onChange={(value) =>
                        onTrainsValChange(
                          value,
                          trainscolumns[trainscolumn].key
                        )
                      }
                      showCancelIcon
                    />
                  ) : (
                    <InputBox
                      labelText={trainscolumns[trainscolumn].name}
                      key={trainscolumns[trainscolumn].key}
                      value={
                        selectedColumns[trainscolumns[trainscolumn].key] || ""
                      }
                      disabled
                      hideClearBtn
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="float-right margin-top-column-selection">
          <DupontButton
            customClass="margin-left-10 "
            disabled={getConfirmButtonState()}
            onClick={onConfirmColumn}
          >
            {t("analyseData.confirmColumnSelection")}
          </DupontButton>
          <DupontButton
            type="tertiary"
            onClick={openDataVisualizationPopup}
            customClass="margin-left-10 "
            disabled={dataVisualisationEnabled}
          >
            {t("analyseData.dataVisualization")}
          </DupontButton>
          {showDataVisualizationPopup && (
            <DataVisualizationTable
              open={showDataVisualizationPopup}
              title={t("analyseData.dataVisualization")}
              rows={dataVisualisationRow}
              setshowDataVisualizationPopup={setshowDataVisualizationPopup}
              setTempReferenceValues={setTempReferenceValues}
              onClose={onCloseClicked}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default ColumnSelection;
